import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpFromBracket,
  faCheck,
  faChevronDown,
  faChevronLeft,
  faCircleInfo,
  faUtensils,
} from '@fortawesome/free-solid-svg-icons';
import { useBranch, useBranches, useShopInfo } from '../../settings/SettingsContext';
import { brandName } from '../../branch/head';
import { displayName, withLang } from '../../branch/name';
import LanguageSwitcher from '../LanguageSwitcher';
import { SprigDivider } from '../Ornaments';
import HeroMedia from './HeroMedia';
import LogoTile from './LogoTile';
import OpenStatus from './OpenStatus';
import BranchInfoSheet from './BranchInfoSheet';
import { ContactRows, HoursTable } from './BranchDetails';
import './branch.css';

const T = {
  gr: {
    viewMenu: 'Δείτε το μενού',
    changeStore: 'Αλλαγή καταστήματος',
    info: 'Πληροφορίες',
    share: 'Κοινοποίηση',
    copied: 'Ο σύνδεσμος αντιγράφηκε',
    copyFailed: 'Η αντιγραφή δεν έγινε. Αντιγράψτε τη διεύθυνση από τον browser.',
    more: 'Ωράριο & διεύθυνση',
    about: 'Το κατάστημα',
  },
  en: {
    viewMenu: 'View menu',
    changeStore: 'Change store',
    info: 'Info',
    share: 'Share',
    copied: 'Link copied',
    copyFailed: 'Could not copy. Copy the address from your browser.',
    more: 'Hours & address',
    about: 'About this store',
  },
};

interface BranchWelcomeProps {
  lang: 'gr' | 'en';
  onLanguageChange: (lang: 'gr' | 'en') => void;
  /** Below the first screen, before the store details (the featured-items carousel). */
  children?: React.ReactNode;
  /** Clock for open-now and today's hours (tests pass a fixed one). */
  now?: Date;
}

/**
 * `/` inside a branch (https://<brand>.menulynx.com/<branch>): a full-screen welcome — hero,
 * logo tile, "<Brand> <Branch>", open-now, the way into the menu — then real, indexable
 * store details below the fold. The info sheet holds everything else.
 */
const BranchWelcome: React.FC<BranchWelcomeProps> = ({ lang, onLanguageChange, children, now }) => {
  const t = T[lang];
  const branch = useBranch();
  const branches = useBranches();
  const info = useShopInfo();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const aboutRef = useRef<HTMLElement>(null);
  const clock = now ?? new Date();

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 2500);
    return () => clearTimeout(timer);
  }, [toast]);

  if (!branch) return null;
  const brand = brandName(info);
  const storeName = displayName(branch, lang);
  const title = `${brand} ${storeName}`.trim();

  const share = async () => {
    const url = `${window.location.origin}/${branch.slug}`;
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ title, url });
        return;
      } catch (err) {
        if ((err as { name?: string })?.name === 'AbortError') return; // the guest closed the share sheet
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setToast(t.copied);
    } catch {
      setToast(t.copyFailed);
    }
  };

  return (
    <main id="main-content" className="branch-welcome">
      <section className="bw-hero" aria-labelledby="bw-title">
        <HeroMedia videoUrl={info.heroVideoUrl} imageUrl={info.heroUrl} lang={lang} />
        <div className="bw-hero__top">
          {branches.length > 1 ? (
            // a plain anchor: the brand root lives outside this router's basename
            <a className="glass-chip glass-chip--small" href={withLang('/', lang)}>
              <FontAwesomeIcon icon={faChevronLeft} aria-hidden="true" />
              {t.changeStore}
            </a>
          ) : <span />}
          <LanguageSwitcher compact selectedLanguage={lang} handleLanguageChange={onLanguageChange} />
        </div>

        <div className="bw-hero__body">
          <LogoTile src={info.logoUrl} name={brand || storeName} />
          <h1 id="bw-title" className="bw-title">
            {brand && <span className="bw-title__brand">{brand}</span>}{' '}
            <span className="bw-title__branch">{storeName}</span>
          </h1>
          <OpenStatus hours={branch.hours} now={clock} lang={lang} className="open-status--on-hero" />
          <Link to="/categories" className="menu-cta menu-cta--pill">
            <span className="menu-cta__icon" aria-hidden="true"><FontAwesomeIcon icon={faUtensils} /></span>
            <span className="menu-cta__text">{t.viewMenu}</span>
          </Link>
          <div className="bw-actions">
            <button type="button" className="glass-chip" onClick={() => setSheetOpen(true)} aria-haspopup="dialog">
              <FontAwesomeIcon icon={faCircleInfo} aria-hidden="true" />
              {t.info}
            </button>
            <button type="button" className="glass-chip" onClick={share}>
              <FontAwesomeIcon icon={faArrowUpFromBracket} aria-hidden="true" />
              {t.share}
            </button>
          </div>
          <button
            type="button"
            className="bw-more"
            onClick={() => aboutRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          >
            {t.more}
            <FontAwesomeIcon icon={faChevronDown} aria-hidden="true" />
          </button>
        </div>
      </section>

      {children && <div className="bw-featured">{children}</div>}

      <section className="bw-about" aria-labelledby="bw-about-title" ref={aboutRef}>
        <h2 id="bw-about-title" className="bw-about__title">{t.about}</h2>
        <SprigDivider className="menu-divider" />
        <div className="bw-card">
          <p className="bw-card__name">{title}</p>
          <ContactRows info={info} lang={lang} />
          {info.infoText && <p className="bw-card__text">{info.infoText}</p>}
          <HoursTable hours={branch.hours} lang={lang} now={clock} />
        </div>
      </section>

      <BranchInfoSheet open={sheetOpen} onClose={() => setSheetOpen(false)} lang={lang} now={clock} />

      <div className={`toast${toast ? ' toast--visible' : ''}`} role="status">
        {toast && (
          <>
            {toast === t.copied && <FontAwesomeIcon icon={faCheck} className="toast__icon" aria-hidden="true" />}
            <span>{toast}</span>
          </>
        )}
      </div>
    </main>
  );
};

export default BranchWelcome;
